import {
  EQUIPMENT_USABILITY,
  HSE_CATEGORIES,
  HSE_DEPARTMENTS,
  HSE_EXTERNAL_AUTHORITIES,
  HSE_PRIORITIES,
  HSE_SUB_CATEGORIES,
  type HseReportInput,
  type HseReportType,
  NC_CLASSES,
  NC_SOURCES,
  NOTIFICATION_DECISIONS,
  PSC_DOCUMENTS,
  PSC_MOUS,
  PSC_OVERALL_FINDINGS,
} from '@felix/domain';
import type { FieldSpec } from './fields.js';
import type { HseOptions } from './types.js';

const HSEQ_SCALE = ['1', '2', '3', '4', '5'] as const;

/**
 * Formun bölümleri (ADR 0049 §1; HSEQ 10 basamağı, ADR 0097 §4): Report Details, türe özgü
 * kart, Initial Assessment, Risk Assessment, Investigation, Linked Records ve Verification.
 * Zorunluluk işaretleri domain'in gönderim kuralıyla aynıdır (`hseMissingForSubmit`).
 */

/** "1. Report Details": tarih ve saat ayrı bileşende; burada kalan alanlar. */
export function generalSpecs(type: HseReportType): FieldSpec[] {
  return [
    { path: 'location', kind: 'text', required: true },
    {
      path: 'department',
      kind: 'select',
      options: HSE_DEPARTMENTS,
      optionsKey: 'departments',
      required: true,
    },
    { path: 'reportedBy', kind: 'text', required: true },
    {
      path: 'category',
      kind: 'select',
      options: HSE_CATEGORIES[type],
      optionsKey: `categories.${type}`,
      required: true,
      label: `category_${type}`,
    },
    ...(type === 'non_conformity'
      ? [
          {
            path: 'source',
            kind: 'select',
            options: NC_SOURCES,
            optionsKey: 'sources',
            required: true,
          } as FieldSpec,
        ]
      : []),
    ...(type === 'deficiency'
      ? [
          {
            path: 'subCategory',
            kind: 'select',
            options: HSE_SUB_CATEGORIES,
            optionsKey: 'subCategories',
            required: true,
          } as FieldSpec,
        ]
      : []),
    {
      path: 'priority',
      kind: 'select',
      options: HSE_PRIORITIES,
      optionsKey: 'priorities',
      required: true,
    },
    { path: 'title', kind: 'text', required: true, full: true, label: 'title' },
    { path: 'description', kind: 'textarea', required: true, full: true, max: 2000, rows: 5 },
  ];
}

/** PSC denetim bilgileri (ADR 0049 §9): kaynak PSC denetimi olan uygunsuzlukta. */
const PSC_SPECS: FieldSpec[] = [
  {
    path: 'details.psc.authority',
    kind: 'text',
    required: true,
    full: true,
    label: 'pscAuthority',
  },
  {
    path: 'details.psc.mou',
    kind: 'select',
    options: PSC_MOUS,
    optionsKey: 'mous',
    required: true,
  },
  { path: 'details.psc.port', kind: 'text', required: true, label: 'pscPort' },
  { path: 'details.psc.inspectionDate', kind: 'date', required: true },
  { path: 'details.psc.inspectionNo', kind: 'text' },
  { path: 'details.psc.inspectorName', kind: 'text', full: true },
  { path: 'details.psc.reportNo', kind: 'text' },
  {
    path: 'details.psc.issuedDocument',
    kind: 'select',
    options: PSC_DOCUMENTS,
    optionsKey: 'pscDocuments',
  },
  { path: 'details.psc.remarkReference', kind: 'text', full: true },
  {
    path: 'details.psc.overallFinding',
    kind: 'select',
    options: PSC_OVERALL_FINDINGS,
    optionsKey: 'pscFindings',
    required: true,
    full: true,
  },
  { path: 'details.psc.findingNote', kind: 'textarea', full: true, rows: 2, max: 2000 },
];

/**
 * Türe özgü alanlar (ortak 2026-10-20b H5–H8; ADR 0097 §5): "Report Details" içinde ayrı kart.
 * Başlığı `hse.hseq.typeDetails.<tür>`; PSC kaynaklı uygunsuzlukta `hse.record.side.psc`.
 */
export function sideSpecs(
  value: HseReportInput,
  _options: HseOptions | null,
): { key: string; specs: FieldSpec[] } {
  switch (value.reportType) {
    case 'non_conformity':
      if (value.source === 'psc_inspection') return { key: 'psc', specs: PSC_SPECS };
      return {
        key: 'sms',
        specs: [
          // H5: ihlal edilen ISM / SMS maddesi zorunlu
          { path: 'details.smsReference', kind: 'text', full: true, required: true, max: 300 },
          {
            path: 'details.ncClass',
            kind: 'select',
            options: NC_CLASSES,
            optionsKey: 'ncClasses',
            full: true,
          },
        ],
      };
    case 'deficiency':
      return {
        key: 'related',
        specs: [
          {
            path: 'details.equipmentUsable',
            kind: 'select',
            options: EQUIPMENT_USABILITY,
            optionsKey: 'usability',
            required: true,
          },
          // H6: operasyonu engelliyor mu, kapatma süresi, PSC / Flag / Class referansı
          {
            path: 'details.impedesOperation',
            kind: 'select',
            options: ['yes', 'no'],
            optionsKey: 'yesNo',
          },
          { path: 'targetDate', kind: 'date', label: 'closeBy' },
          {
            path: 'details.externalAuthority',
            kind: 'select',
            options: HSE_EXTERNAL_AUTHORITIES,
            optionsKey: 'externalAuthorities',
          },
          { path: 'details.externalReference', kind: 'text' },
          { path: 'details.assetNo', kind: 'text' },
          { path: 'details.locationDetail', kind: 'text', full: true },
          { path: 'details.operationalImpact', kind: 'textarea', full: true, rows: 2 },
        ],
      };
    case 'near_miss':
      return {
        key: 'risk',
        specs: [
          // H7: potansiyel zarar, koşullar, insan faktörleri (alınan ders incelemede)
          {
            path: 'details.worstOutcome',
            kind: 'textarea',
            required: true,
            full: true,
            rows: 2,
            label: 'potentialHarm',
          },
          {
            path: 'details.riskLevel',
            kind: 'select',
            options: HSE_PRIORITIES,
            optionsKey: 'priorities',
            required: true,
          },
          { path: 'details.affected', kind: 'textarea', full: true, rows: 2 },
          { path: 'details.conditions', kind: 'textarea', full: true, rows: 2 },
          { path: 'details.humanFactors', kind: 'textarea', full: true, rows: 2 },
        ],
      };
    case 'accident':
      return {
        key: 'accident',
        specs: [
          // H8: yaralanma, ekipman hasarı, çevre kirliliği
          { path: 'details.injuredCount', kind: 'number' },
          { path: 'details.injuries', kind: 'textarea', full: true, rows: 2 },
          {
            path: 'details.damage',
            kind: 'textarea',
            full: true,
            rows: 2,
            label: 'equipmentDamage',
          },
          {
            path: 'details.environmental',
            kind: 'textarea',
            full: true,
            rows: 2,
            label: 'pollution',
          },
        ],
      };
  }
}

/**
 * 2. Initial Assessment (H2; ADR 0097 §4): ilk müdahale (acil tedbir), kaptanın ilk
 * değerlendirmesi; eksiklikte geçici tedbir, kazada ilk müdahale ve tıbbi tedbir. Kaza bildirimi
 * ayrı düzenleyicide (H8: soruşturmayı beklemez).
 */
export function initialSpecs(type: HseReportType): FieldSpec[] {
  const required = type === 'near_miss' || type === 'accident';
  return [
    { path: 'immediateAction', kind: 'textarea', full: true, rows: 2, required },
    ...(type === 'deficiency'
      ? [{ path: 'details.temporaryMeasure', kind: 'textarea', full: true, rows: 2 } as FieldSpec]
      : []),
    ...(type === 'accident'
      ? [
          {
            path: 'details.emergencyResponse',
            kind: 'textarea',
            full: true,
            rows: 2,
            label: 'firstResponse',
          } as FieldSpec,
          { path: 'details.medicalMeasures', kind: 'textarea', full: true, rows: 2 } as FieldSpec,
        ]
      : []),
    { path: 'details.initialAssessment', kind: 'textarea', full: true, rows: 3 },
  ];
}

/** 3. Risk Assessment: olası sonuçlar, mevcut kontroller, olasılık ve şiddet (seviye türer). */
export const RISK_SPECS: FieldSpec[] = [
  { path: 'details.risk.consequences', kind: 'textarea', rows: 2, max: 2000 },
  { path: 'details.risk.existingControls', kind: 'textarea', rows: 2, max: 2000 },
  {
    path: 'details.risk.likelihood',
    kind: 'select',
    options: HSEQ_SCALE,
    optionsKey: 'likelihoodScale',
    numeric: true,
  },
  {
    path: 'details.risk.severity',
    kind: 'select',
    options: HSEQ_SCALE,
    optionsKey: 'severityScale',
    numeric: true,
  },
];

/** 4. Investigation / Root Cause: inceleme, kök neden, türe özgü analiz, alınan ders. */
export function investigationSpecs(type: HseReportType, _options: HseOptions | null): FieldSpec[] {
  const typed: Record<HseReportType, FieldSpec[]> = {
    non_conformity: [
      { path: 'details.rootCauseHypothesis', kind: 'textarea', full: true, rows: 2 },
      { path: 'details.proposedAction', kind: 'textarea', full: true, rows: 2 },
    ],
    deficiency: [
      { path: 'details.manufacturer', kind: 'text' },
      { path: 'details.model', kind: 'text' },
      { path: 'details.spareEquipment', kind: 'text' },
      { path: 'details.serviceNeed', kind: 'textarea', full: true, rows: 2 },
    ],
    near_miss: [
      { path: 'details.chronology', kind: 'textarea', full: true, rows: 3, max: 8000 },
      { path: 'details.preventingFactors', kind: 'textarea', full: true, rows: 2 },
      { path: 'details.similarEvents', kind: 'textarea', full: true, rows: 2 },
      { path: 'details.shareWithFleet', kind: 'bool' },
    ],
    accident: [
      {
        path: 'details.chronology',
        kind: 'textarea',
        full: true,
        rows: 3,
        max: 8000,
        label: 'timeline',
      },
      { path: 'details.witnesses', kind: 'textarea', full: true, rows: 3, max: 8000 },
      { path: 'details.contributingFactors', kind: 'textarea', full: true, rows: 2 },
      { path: 'details.losses', kind: 'text', full: true },
    ],
  };
  return [
    { path: 'investigation', kind: 'textarea', full: true, rows: 4, max: 8000 },
    { path: 'rootCause', kind: 'textarea', full: true, rows: 3 },
    ...typed[type],
    { path: 'lessonsLearned', kind: 'textarea', full: true, rows: 2 },
    { path: 'responsible', kind: 'text' },
    ...(type === 'deficiency' ? [] : [{ path: 'targetDate', kind: 'date' } as FieldSpec]),
  ];
}

/** 6. Linked Records: var olan kayda bağlama (ekipman, arıza, iş emri, form, ana olay). */
export function equipmentSpec(options: HseOptions | null): FieldSpec {
  return {
    path: 'equipmentId',
    kind: 'select',
    label: 'relatedAsset',
    choices: (options?.equipment ?? []).map((e) => ({
      value: e.id,
      label: `${e.code} · ${e.name}`,
    })),
  };
}

export function linkSpecs(options: HseOptions | null): FieldSpec[] {
  return [
    {
      path: 'defectId',
      kind: 'select',
      label: 'pmsDefect',
      choices: (options?.defects ?? []).map((d) => ({
        value: d.id,
        label: `${d.no} · ${d.title}`,
      })),
    },
    {
      path: 'workOrderId',
      kind: 'select',
      label: 'pmsWorkOrder',
      choices: (options?.workOrders ?? []).map((w) => ({
        value: w.id,
        label: `${w.no} · ${w.title}`,
      })),
    },
    {
      path: 'formRecordId',
      kind: 'select',
      label: 'formRecord',
      choices: (options?.formRecords ?? []).map((f) => ({
        value: f.id,
        label: `${f.no} · ${f.title}`,
      })),
    },
    {
      path: 'parentReportId',
      kind: 'select',
      label: 'parentReport',
      full: true,
      choices: (options?.reports ?? []).map((r) => ({
        value: r.id,
        label: `${r.no} · ${r.title}`,
      })),
    },
  ];
}

/** Kaza bildirimi kararı (ADR 0049 §10); bildirim listesi ayrı düzenleyicide. */
export const NOTIFICATION_SPECS: FieldSpec[] = [
  {
    path: 'details.notification.decision',
    kind: 'select',
    options: NOTIFICATION_DECISIONS,
    optionsKey: 'notificationDecisions',
    label: 'notificationDecision',
  },
  {
    path: 'details.notification.reason',
    kind: 'textarea',
    rows: 2,
    full: true,
    label: 'notificationReason',
  },
];

/** 8. Verification: tarih, doğrulayan, açıklama ve türe özgü kapanış alanları (etkinlik ayrı). */
export function verificationSpecs(type: HseReportType): FieldSpec[] {
  return [
    { path: 'details.verification.date', kind: 'date', label: 'verificationDate' },
    { path: 'details.verification.verifiedBy', kind: 'text', label: 'verifiedBy' },
    { path: 'verificationNote', kind: 'textarea', full: true, rows: 3 },
    ...(type === 'deficiency'
      ? [
          { path: 'details.testResult', kind: 'textarea', full: true, rows: 2 } as FieldSpec,
          {
            path: 'details.fitForService',
            kind: 'select',
            options: ['yes', 'no'],
            optionsKey: 'yesNo',
          } as FieldSpec,
        ]
      : []),
    ...(type === 'near_miss'
      ? [
          {
            path: 'details.residualRisk',
            kind: 'select',
            options: HSE_PRIORITIES,
            optionsKey: 'priorities',
          } as FieldSpec,
        ]
      : []),
  ];
}

/** Yeni raporun boş değeri; gemi saati farkıyla. */
export function emptyReport(
  type: HseReportType,
  tzOffsetMin: number,
  extra: Partial<HseReportInput> = {},
): HseReportInput {
  return {
    reportType: type,
    title: null,
    description: null,
    occurredAt: null,
    tzOffsetMin,
    location: null,
    department: null,
    reportedBy: null,
    category: type === 'non_conformity' ? null : null,
    subCategory: null,
    priority: null,
    source: null,
    responsible: null,
    targetDate: null,
    immediateAction: null,
    investigation: null,
    rootCause: null,
    verificationNote: null,
    lessonsLearned: null,
    details: {},
    parentReportId: null,
    equipmentId: null,
    workOrderId: null,
    defectId: null,
    certificateId: null,
    formRecordId: null,
    ...extra,
  };
}

/** Olay anı (UTC) ↔ gemi yerel gün ve saati. */
export function localParts(
  occurredAt: string | null,
  tzOffsetMin: number,
): { day: string | null; time: string } {
  if (!occurredAt) return { day: null, time: '' };
  const local = new Date(Date.parse(occurredAt) + tzOffsetMin * 60_000).toISOString();
  return { day: local.slice(0, 10), time: local.slice(11, 16) };
}

export function occurredAtOf(day: string | null, time: string, tzOffsetMin: number): string | null {
  if (!day) return null;
  const hhmm = /^\d{2}:\d{2}$/.test(time) ? time : '00:00';
  const utc = Date.parse(`${day}T${hhmm}:00.000Z`) - tzOffsetMin * 60_000;
  return new Date(utc).toISOString();
}
